<template lang="pug">
.v-video.video
	template(v-if="isStarted")
		.video__iframe-wrap(v-if="(type === 'youtube' || type === 'rutube') && src")
			template(v-if="linked")
				iframe.video__iframe(ref="ytvideo"
						:src="youtubeSrc || rutubeSrc"
						title="Video player"
						frameborder="0"
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
						referrerpolicy="strict-origin-when-cross-origin"
						allowfullscreen)
				a.video__iframe-overlay(:href="transformUrlForApp || rutubeSrc"
						target="_blank"
						@click="handleClick(index)")
				.video__iframe-control-hider(v-if="!isSm && rutubeSrc")
			template(v-else-if="modalClass")
				iframe.video__iframe(ref="ytvideo"
						:src="youtubeSrc || rutubeSrc"
						title="YouTube video player"
						frameborder="0"
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
						referrerpolicy="strict-origin-when-cross-origin"
						allowfullscreen)
				.video__iframe-overlay(@click="handleClick(index)")
				.video__iframe-control-hider(v-if="!isSm && rutubeSrc")
			template(v-else)
				iframe.video__iframe(ref="ytvideo"
						:src="youtubeSrc || rutubeSrc"
						title="YouTube video player"
						frameborder="0"
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
						referrerpolicy="strict-origin-when-cross-origin"
						allowfullscreen)
		template(v-else-if="src && !modal")
			video.video__video(ref="video"
					:poster="poster"
					:controls="controls"
					:loop="loop"
					:muted="localMuted"
					:autoplay="autoplay")
				source.video__source(:src="src" :type="type")
			.video__iframe-overlay(@click="handleClick(index)")
		template(v-else-if="src && modal")
			video.video__video(ref="video"
					:poster="poster"
					:controls="controls"
					:loop="loop"
					:muted="localMuted"
					:autoplay="autoplay")
				source.video__source(:src="src" :type="type")
			v-svg-icon.video__btn-loud(v-if="!localMuted"
					icon="volume-loud"
					@click="toggleMute")
			v-svg-icon.video__btn-loud(v-if="localMuted"
					icon="volume-cross"
					@click="toggleMute")
	template(v-else)
		img.video__poster(:src="poster" alt="" loading="lazy")
		button.video__btn(type="button" @click="startVideo")
			v-svg-icon.video__btn-icon(icon="play-btn")
</template>

<script>
import controller from '../../scripts/controller/controller.js';
import uniqueId from '../../scripts/helpers/uniqueId.js';
import isMobMixin from '../../components/mixins/v-mixin-is-mob.vue';

export default {
	props: {
		type: {
			type: String,
			default: 'video/mp4'
		},
		poster: {
			type: String,
			default: null
		},
		src: {
			type: String,
			default: null,
			required: true
		},
		autoplay: {
			type: Boolean,
			default: false
		},
		controls: {
			type: Boolean,
			default: true
		},
		loop: {
			type: Boolean,
			default: false
		},
		origin: {
			type: String,
			default: null
		},
		linked: {
			type: [Boolean, String],
			default: null
		},
		modalClass: {
			type: [Boolean, String],
			default: false
		},
		modal: {
			type: Boolean,
			default: false
		},
		modalProp: {
			type: Object,
			default: null
		},
		index: {
			type: Number,
			default: null
		},
		muted: {
			type: Boolean,
			default: false
		}
	},

	mixins: [
		isMobMixin,
	],

	data() {
		return {
			uniqueId: uniqueId('v-video-'),
			isStarted: false,
			youtubeSrc: null,
			youtubeLink: null,
			rutubeSrc: null,
			localMuted: this.muted
		};
	},

	created() {
		if (this.autoplay) {
			this.isStarted = true;
		}
		this.initVideo();
	},

	computed: {
		transformUrlForApp() {
			if (!this.youtubeLink) {
				return null;
			}
			const videoId = this.youtubeLink.split('/').pop().split('?')[0];
			const params = this.youtubeLink.split('?')[1];

			return `https://www.youtube.com/watch?v=${videoId}${params ? '?' + params : ''}`;
		},

		isSafari() {
			return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
		},
	},

	methods: {
		initVideo() {
			if (this.type === 'youtube') {
				const url = new URL(this.src);
				let {pathname} = url;
				if (pathname && pathname.indexOf('/embed') === -1) {
					url.pathname = `/embed${pathname}`;
				}
				url.searchParams.set('autoplay', this.autoplay ? '1' : '0');
				url.searchParams.set('mute', this.localMuted ? '1' : '0');
				url.searchParams.set('controls', this.controls ? '1' : '0');
				url.searchParams.set('loop', this.loop ? '1' : '0');
				url.searchParams.set('rel', '0');
				url.searchParams.set('enablejsapi', '1');
				url.searchParams.set('showinfo', '0');
				if (this.origin) {
					url.searchParams.set('origin', 'https://yourdomain.by');
				}
				url.searchParams.set('playsinline', '1');
				if (this.linked) {
					url.searchParams.set('controls', '0');
					this.youtubeSrc = url.href;
					url.searchParams.set('controls', '1');
					url.searchParams.set('autoplay', '1');
					url.searchParams.set('mute', '0');
					this.youtubeLink = url.href;
				} else {
					this.youtubeSrc = url.href;
				}
				if(this.autoplay) {
					this.playVideo();
				}
			} else if (this.type === 'rutube') {
				const originalUrl = this.src;
				const url = new URL(originalUrl);
				const pathParts = url.pathname.split('/').filter(Boolean);
				const searchParams = new URLSearchParams(url.search);
				let videoId;
				let isPrivate = false;
				if (pathParts[0] === 'video' && pathParts[1] === 'private') {
					videoId = pathParts[2];
					isPrivate = true;
				} else if (pathParts[0] === 'video') {
					videoId = pathParts[1];
				}
				if (!videoId) throw new Error('Missing videoId');
				if (this.autoplay) {
					if(this.isiOS && !this.muted) {
						searchParams.set('autoplay', 'false');
					} else {
						searchParams.set('autoplay', 'true');
					}
				}
				let newUrl = `https://rutube.ru/play/embed/${videoId}/`;
				if (searchParams.toString()) {
					newUrl += `?${searchParams.toString()}`;
				}
				this.rutubeSrc = newUrl;

				this.$nextTick(() => {
					setTimeout(() => {
						const $player = this.$refs.ytvideo;
						if ($player) {
							$player.contentWindow.postMessage(
								JSON.stringify({
									type: this.muted ? 'player:mute' : 'player:unMute',
									data: {}
								}),
								'*'
							);
							$player.contentWindow.postMessage(
								JSON.stringify({
									type: 'player:setVolume',
									data: {volume: 1}
								}),
								'*'
							);
						}
					}, 1000);
				});
			}
		},

		playVideo() {
			this.$nextTick(() => {
				setTimeout(() => {
					if (this.$refs.ytvideo && this.$refs.ytvideo.contentWindow) {
						try {
							this.$refs.ytvideo.contentWindow.postMessage(
								JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
								'*'
							);
						} catch (error) {
							console.error('Ошибка при отправке сообщения в iframe:', error);
						}
					} else {
						console.warn('YouTube iframe или contentWindow не найдены');
					}
				}, 1000);
			});
		},

		startVideo() {
			this.isStarted = true;
			this.$nextTick(() => {
				if (this.type === 'youtube') {
					const url = new URL(this.youtubeSrc);
					url.searchParams.set('autoplay', '1');
					this.youtubeSrc = url.href;
				} else {
					this.$refs['video'].play();
				}
			});
		},

		toggleMute() {
			this.localMuted = !this.localMuted;
		},

		handleClick(index) {
			controller.clickVideo(index);
			if (this.modalClass) {
				//Stop current video before open modal
				if(this.$refs.ytvideo) {
					if(this.type === 'youtube') {
						this.$refs.ytvideo.contentWindow.postMessage(
							JSON.stringify({ event: 'command', func: 'stopVideo', args: [] }),
							'*'
						);
					} else if(this.type === 'rutube') {
						this.$refs.ytvideo.contentWindow.postMessage(
							JSON.stringify({
								type: 'player:stop',
								data: {}
							}),
							'*'
						);
					}
				}
				controller.openModal({
					className: this.modalClass,
					component: {
						name: 'v-video',
						props: {type: this.type, src: this.src, ...this.modalProp}
					}
				});
			}
		}
	}
};
</script>